<app-menu>
  <div class="cargo-shell">
    <section class="cargo-panel">
      <div class="cargo-heading">
        <div>
          <h1>C.A.R.G.O.</h1>
          <p>{{ isDateRangeSearch ? 'Date range export' : (parsedSearchValues.length | number) + ' search values ready' }}</p>
        </div>
      </div>

      <form class="cargo-form" [formGroup]="cargoForm" (ngSubmit)="submitCargoReport()">
        <div class="cargo-grid">
          <div class="cargo-field cargo-field--wide">
            <label>Customer</label>
            <app-contact-autocomplete
              placeholder="Search customer"
              [type]="customerContactType"
              panelTheme="dark"
              formControlName="customerContact"
              (selected)="onCustomerSelected($event)"
            ></app-contact-autocomplete>
            <p *ngIf="controlInvalid('customerContact')" class="cargo-error">Customer is required.</p>
          </div>

          <mat-form-field appearance="outline" class="cargo-material-field">
            <mat-label>Search By</mat-label>
            <mat-select formControlName="search_by">
              <mat-option *ngFor="let option of searchByOptions" [value]="option.value">
                {{ option.label }}
              </mat-option>
            </mat-select>
          </mat-form-field>

          <ng-container *ngIf="isDateRangeSearch; else valueSearch">
            <mat-form-field appearance="outline" class="cargo-material-field">
              <mat-label>Date From</mat-label>
              <input matInput [matDatepicker]="dateFromPicker" formControlName="date_from">
              <mat-datepicker-toggle matSuffix [for]="dateFromPicker"></mat-datepicker-toggle>
              <mat-datepicker #dateFromPicker></mat-datepicker>
              <mat-error *ngIf="controlInvalid('date_from')">Date from is required.</mat-error>
            </mat-form-field>

            <mat-form-field appearance="outline" class="cargo-material-field">
              <mat-label>Date To</mat-label>
              <input matInput [matDatepicker]="dateToPicker" formControlName="date_to">
              <mat-datepicker-toggle matSuffix [for]="dateToPicker"></mat-datepicker-toggle>
              <mat-datepicker #dateToPicker></mat-datepicker>
              <mat-error *ngIf="controlInvalid('date_to')">Date to is required.</mat-error>
            </mat-form-field>
          </ng-container>

          <ng-template #valueSearch>
            <mat-form-field appearance="outline" class="cargo-material-field cargo-field--wide">
              <mat-label>Search Values</mat-label>
              <textarea
                matInput
                rows="8"
                formControlName="raw_search_values"
                placeholder="One per line, tab, comma, or semicolon"
              ></textarea>
              <mat-hint>{{ parsedSearchValues.length | number }} values detected</mat-hint>
              <mat-error *ngIf="controlInvalid('raw_search_values')">At least one search value is required.</mat-error>
            </mat-form-field>
          </ng-template>
        </div>

        <div class="cargo-preview-choice">
          <div class="cargo-preview-copy">
            <h2>Preview before storing</h2>
            <p>Review the lading financials first. Generate only after the preview looks correct.</p>
          </div>

          <mat-radio-group class="cargo-preview-options" formControlName="preview">
            <mat-radio-button class="cargo-preview-option cargo-preview-option--recommended" [value]="true">
              <span class="cargo-preview-title">Preview only</span>
              <span class="cargo-preview-note">Recommended first step</span>
            </mat-radio-button>
            <mat-radio-button class="cargo-preview-option cargo-preview-option--final" [value]="false">
              <span class="cargo-preview-title">Generate and store</span>
              <span class="cargo-preview-note">Permanent report data</span>
            </mat-radio-button>
          </mat-radio-group>
        </div>

        <div class="cargo-actions">
          <button mat-raised-button color="primary" type="submit" [disabled]="submitting || resolving">
            {{ submitting ? 'Sending...' : (cargoForm.get('preview')?.value ? 'Preview Report' : 'Generate Report') }}
          </button>
          <button mat-stroked-button color="primary" type="button" (click)="resetForm()" [disabled]="submitting || resolving">
            Reset
          </button>
        </div>
      </form>

      <section *ngIf="unresolvedGroups.length > 0" class="cargo-resolution">
        <div class="cargo-resolution-heading">
          <div>
            <h2>Unresolved Ladings</h2>
            <p>{{ unresolvedGroups.length | number }} inputs need a lading selection.</p>
          </div>
          <button
            mat-raised-button
            color="primary"
            type="button"
            (click)="submitResolvedLadings()"
            [disabled]="!canSubmitResolvedLadings"
          >
            {{ resolving ? 'Sending...' : 'Send Resolved POST' }}
          </button>
        </div>

        <div *ngIf="unresolvableInputCount > 0" class="cargo-alert">
          {{ unresolvableInputCount | number }} unresolved inputs have no lading options.
        </div>

        <mat-accordion>
          <mat-expansion-panel
            *ngFor="let group of unresolvedGroups; trackBy: trackUnresolvedGroup"
            [expanded]="unresolvedGroups.length === 1"
          >
            <mat-expansion-panel-header>
              <mat-panel-title>{{ group.originalValue }}</mat-panel-title>
              <mat-panel-description>
                {{ group.ladings.length | number }} candidates
              </mat-panel-description>
            </mat-expansion-panel-header>

            <div *ngIf="group.ladings.length === 0" class="cargo-empty">
              No lading options were returned for this value.
            </div>

            <mat-radio-group
              class="cargo-lading-options"
              [value]="selectedLadingFor(group.originalValue)"
              (change)="selectResolvedLading(group.originalValue, $event.value)"
            >
              <mat-radio-button
                *ngFor="let lading of group.ladings; trackBy: trackLading"
                class="cargo-lading-option"
                [class.cargo-lading-option--selected]="selectedLadingFor(group.originalValue) === lading.lading_uuid"
                [value]="lading.lading_uuid"
              >
                <div class="cargo-lading-details">
                  <div class="cargo-lading-title">
                    <span>{{ lading.lading_uuid }}</span>
                    <span *ngIf="lading.alternate_reference">Alt: {{ lading.alternate_reference }}</span>
                    <span *ngIf="lading.customer_po">PO: {{ lading.customer_po }}</span>
                  </div>
                  <div class="cargo-lading-route">
                    <span>{{ lading.shipper_name || 'Shipper' }}</span>
                    <span>{{ lading.shipper_location }}</span>
                    <span>{{ lading.pickup_datetime }}</span>
                  </div>
                  <div class="cargo-lading-route">
                    <span>{{ lading.consignee_name || 'Consignee' }}</span>
                    <span>{{ lading.consignee_location }}</span>
                    <span>{{ lading.delivery_datetime }}</span>
                  </div>
                </div>
              </mat-radio-button>
            </mat-radio-group>
          </mat-expansion-panel>
        </mat-accordion>
      </section>
    </section>
  </div>
</app-menu>